import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import { UserRole } from '@packages/enums';
import { AppMode } from '@ui/constants';

export interface UserState {
    id: string | null;
    name: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    isAuthenticated: boolean;
    roles: UserRole[];
    appModePreference: AppMode | null;
}

const initialState: UserState = {
    id: null,
    name: null,
    email: null,
    firstName: null,
    lastName: null,
    username: null,
    isAuthenticated: false,
    roles: [],
    appModePreference: null,
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUser: (state, action: PayloadAction<Omit<UserState, 'isAuthenticated'>>) => {
            state.id = action.payload.id;
            state.name = action.payload.name;
            state.email = action.payload.email;
            state.firstName = action.payload.firstName;
            state.lastName = action.payload.lastName;
            state.username = action.payload.username;
            state.roles = action.payload.roles;
            state.appModePreference = action.payload.appModePreference;
            state.isAuthenticated = true;
        },
        setAppModePreference: (state, action: PayloadAction<AppMode | null>) => {
            state.appModePreference = action.payload;
        },
        clearUser: state => {
            state.id = null;
            state.name = null;
            state.email = null;
            state.firstName = null;
            state.lastName = null;
            state.username = null;
            state.roles = [];
            state.appModePreference = null;
            state.isAuthenticated = false;
        },
    },
});

export const { setUser, setAppModePreference, clearUser } = userSlice.actions;

// Selectors
export const selectUser = (state: RootState) => state.user;
export const selectIsAuthenticated = (state: RootState) => state.user.isAuthenticated;
export const selectUserName = (state: RootState) => state.user.name;
export const selectUserEmail = (state: RootState) => state.user.email;
export const selectIsSystemAdmin = (state: RootState) => state.user.roles.includes(UserRole.SYSTEM_ADMINISTRATION);

export const selectHasConversationInsights = (state: RootState) =>
    state.user.roles.includes(UserRole.CONVERSATION_INSIGHTS);

export const selectHasDataManagement = (state: RootState) => state.user.roles.includes(UserRole.DATA_MANAGEMENT);

export const selectHasUmaProtection = (state: RootState) => state.user.roles.includes(UserRole.UMA_PROTECTION);

export const selectAppMode = (state: RootState): AppMode => {
    if (state.user.appModePreference === AppMode.PATIENT_JOURNEY) {
        return AppMode.PATIENT_JOURNEY;
    }

    if (
        state.user.appModePreference === AppMode.PATIENT_REGISTRY &&
        state.user.roles.includes(UserRole.PATIENT_REGISTRY)
    ) {
        return AppMode.PATIENT_REGISTRY;
    }

    return AppMode.PATIENT_JOURNEY;
};

export default userSlice.reducer;
